import { createFileRoute, notFound } from "@tanstack/react-router";
import { breadcrumbSchema } from "@/lib/schema";
import { faqSchema } from "@/components/PageFaq";
import { TopicArticle } from "@/components/pages/TopicArticle";
import { findTopic } from "@/lib/topic-pages";
import { NotFoundView } from "@/components/RouteFallbacks";

const BASE = "https://palstars.com/palworld-tcg";

export const Route = createFileRoute("/palworld-tcg/$topic")({
  loader: async ({ params }) => {
    const { TCG_PAGES } = await import("@/lib/tcg-pages");
    const page = findTopic(TCG_PAGES, params.topic);
    if (!page) throw notFound();
    return { page };
  },
  head: ({ params, loaderData }) => {
    const page = loaderData?.page;
    const url = `${BASE}/${params.topic}`;
    if (!page) {
      return {
        meta: [
          { title: "Palworld TCG Page Not Found | Pal Stars" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    return {
      meta: [
        { title: page.title },
        { name: "description", content: page.description },
        { property: "og:title", content: page.h1 },
        { property: "og:description", content: page.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: page.h1 },
        { name: "twitter:description", content: page.description },
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbSchema([
          ["Home", "/"],
          ["Palworld TCG", "/palworld-tcg"],
          [page.h1, `/palworld-tcg/${page.slug}`],
        ]),
        faqSchema(page.faq, url),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${url}#article`,
            url,
            headline: page.h1,
            description: page.description,
            about: { "@type": "Thing", name: "Palworld Official Card Game" },
            articleSection: page.sections.map((s) => s.heading),
            isPartOf: { "@type": "WebSite", name: "Pal Stars", url: "https://palstars.com" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SpeakableSpecification",
            cssSelector: [".aeo-answer"],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <NotFoundView title="Palworld TCG page not found" message="That card game page does not exist. Browse the Palworld TCG hub instead." />
  ),
  component: TcgTopicPage,
});

function TcgTopicPage() {
  const { page } = Route.useLoaderData();
  return (
    <TopicArticle
      page={page}
      eyebrow="Palworld TCG"
      parent={{ to: "/palworld-tcg", label: "Palworld TCG" }}
      faqHeading={`${page.h1} FAQ`}
    />
  );
}
